/**
 * Unified analytics: Yandex.Metrika (browser) + Meta Pixel (browser).
 * Counter / pixel IDs are loaded in index.html.
 */
const YM_ID = 109041828;
const META_PIXEL_ID = "946200998406056";

type YmFn = (id: number, action: string, ...args: unknown[]) => void;
type FbqFn = (...args: unknown[]) => void;

declare global {
  interface Window {
    ym?: YmFn;
    fbq?: FbqFn;
  }
}

// ---------- Meta helpers ----------

function newEventId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

/** Fire a Meta event through the browser Pixel only. */
export function trackMeta(
  eventName: string,
  customData: Record<string, unknown> = {},
): void {
  if (typeof window === "undefined") return;
  const eventId = newEventId();

  // Pixel (browser)
  try {
    window.fbq?.("track", eventName, customData, { eventID: eventId });
  } catch {
    /* ignore */
  }
}

// ---------- Yandex helpers (existing API, now also dispatching to Meta) ----------

/** Fire a Yandex Metrika goal. Safe no-op if ym() isn't loaded yet. */
export function trackGoal(goal: string, params?: Record<string, unknown>): void {
  if (typeof window === "undefined") return;
  try {
    window.ym?.(YM_ID, "reachGoal", goal, params);
  } catch {
    /* ignore */
  }
}

/** Track a WhatsApp click — Yandex goal + Meta `Contact`. */
export function trackWhatsappClick(context?: Record<string, unknown>): void {
  const params = {
    path: typeof window !== "undefined" ? window.location.pathname : undefined,
    ...context,
  };
  trackGoal("whatsapp_click", params);
  trackMeta("Contact", { method: "whatsapp", ...params });
}

/**
 * Track a form submission — Yandex goal + Meta `Lead` (or `Purchase` if
 * context.value is set, e.g. pack quote).
 */
export function trackFormSubmit(form: string, context?: Record<string, unknown>): void {
  const params = { form, ...context };
  trackGoal("form_submit", params);

  const hasValue = typeof context?.value === "number";
  trackMeta(hasValue ? "Purchase" : "Lead", {
    content_name: form,
    currency: "MAD",
    ...params,
  });
}

/** Track when a user starts a pack quote (modal opened / page entered). */
export function trackInitiateCheckout(packId: string, value?: number): void {
  trackGoal("initiate_checkout", { pack: packId, value });
  trackMeta("InitiateCheckout", {
    content_ids: [packId],
    content_type: "product",
    currency: "MAD",
    value,
  });
}

/** Track a content view (e.g. service / pack page). */
export function trackViewContent(name: string, extra?: Record<string, unknown>): void {
  trackGoal("view_content", { name, ...extra });
  trackMeta("ViewContent", { content_name: name, ...extra });
}

/** Hit a virtual pageview (useful for SPA route changes). */
export function trackPageview(url?: string): void {
  if (typeof window === "undefined") return;
  try {
    window.ym?.(YM_ID, "hit", url ?? window.location.href, {
      referer: document.referrer,
    });
  } catch {
    /* ignore */
  }
  // Meta PageView for SPA routes
  trackMeta("PageView", { path: window.location.pathname });
}

/**
 * Install scroll-depth tracking on the current page.
 * Fires goals: scroll_25, scroll_50, scroll_75, scroll_100 — each at most once.
 * scroll_75 also dispatches Meta `ViewContent` for funnel analysis.
 */
export function installScrollDepthTracking(): () => void {
  if (typeof window === "undefined") return () => {};
  const thresholds = [25, 50, 75, 100];
  const fired = new Set<number>();

  const onScroll = () => {
    const doc = document.documentElement;
    const scrollTop = window.scrollY || doc.scrollTop;
    const viewport = window.innerHeight || doc.clientHeight;
    const full = doc.scrollHeight - viewport;
    if (full <= 0) return;
    const pct = Math.min(100, Math.round((scrollTop / full) * 100));
    for (const t of thresholds) {
      if (pct >= t && !fired.has(t)) {
        fired.add(t);
        trackGoal(`scroll_${t}`, { path: window.location.pathname });
        if (t === 75) {
          trackMeta("ViewContent", {
            content_name: `scroll_75:${window.location.pathname}`,
          });
        }
      }
    }
    if (fired.size === thresholds.length) {
      window.removeEventListener("scroll", onScroll);
    }
  };

  let raf = 0;
  const handler = () => {
    if (raf) return;
    raf = window.requestAnimationFrame(() => {
      raf = 0;
      onScroll();
    });
  };

  window.addEventListener("scroll", handler, { passive: true });
  onScroll();

  return () => window.removeEventListener("scroll", handler);
}

/**
 * Dev helper: window.__analytics for manual goal firing.
 */
if (typeof window !== "undefined") {
  (window as unknown as { __analytics?: unknown }).__analytics = {
    goal: trackGoal,
    meta: trackMeta,
    whatsapp: (source = "test") => trackWhatsappClick({ source, debug: true }),
    form: (name = "test") => trackFormSubmit(name, { debug: true }),
    checkout: (pack = "test", value = 0) => trackInitiateCheckout(pack, value),
    view: (name = "test") => trackViewContent(name),
    pageview: trackPageview,
    ymLoaded: () => typeof window.ym === "function",
    fbqLoaded: () => typeof window.fbq === "function",
    pixelId: META_PIXEL_ID,
  };
}
